import { useState } from "react";
import { Link } from "react-router-dom";
import { LockKeyhole } from "lucide-react";
import { useDispatch } from "react-redux";
import { useUser } from "../../../app/providers/userContext";
import { setUserData } from "../../../app/store/slices/userSlice";
import { toReduxUser } from "../../../shared/hooks/useAccountSwitcher";
import { canRenewSubscription } from "../../../shared/constants/subscriptionAccess";
import axiosInstance from "../../../services/api/axiosInstance";

export default function SubscriptionNotice() {
  const { user, login } = useUser();
  const dispatch = useDispatch();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const refresh = async () => {
    setChecking(true);
    setError("");
    setMessage("");
    try {
      const response = await axiosInstance.get("/auth/validate-token");
      const { success, user: updated, token } = response.data || {};
      if (!success || !updated || !Array.isArray(updated.roles) || typeof updated.subscriptionStatus !== "string") {
        throw new Error("Subscription status could not be verified.");
      }
      login({ ...updated, token: token || localStorage.getItem("token") });
      dispatch(setUserData(toReduxUser(updated)));
      setMessage("Status checked. Access will resume when your school's renewal is confirmed.");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Unable to check subscription status.");
    } finally {
      setChecking(false);
    }
  };
  return (
    <section className="mx-auto w-full max-w-2xl p-4 sm:p-6" aria-labelledby="subscription-title">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-8">
        <LockKeyhole size={28} className="mb-4 text-slate-500" aria-hidden="true" />
        <h1 id="subscription-title" className="text-xl font-semibold text-slate-900">Subscription renewal required</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          Your school's subscription has expired or payment is due. Operational modules are paused until renewal is confirmed. Your records have not been deleted.
        </p>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          {canRenewSubscription(user)
            ? "Renew your school's subscription to restore access for staff and students."
            : "Please contact your school admin to renew. You can still access your profile, switch profiles, and contact support."}
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          {canRenewSubscription(user) && <Link to="/admin/setup/pricing" className="inline-flex min-h-11 items-center rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white">Renew subscription</Link>}
          <Link to="/support" className="inline-flex min-h-11 items-center rounded-lg border border-slate-300 px-4 text-sm font-medium text-slate-700">Contact support</Link>
          <button type="button" disabled={checking} onClick={refresh} className="min-h-11 rounded-lg border border-slate-300 px-4 text-sm font-medium text-slate-700 disabled:opacity-50">{checking ? "Checking..." : "Check renewal status"}</button>
        </div>
        {error && <p role="alert" className="mt-4 text-sm text-red-700">{error}</p>}
        {message && <p role="status" className="mt-4 text-sm text-slate-600">{message}</p>}
      </div>
    </section>
  );
}
